import React, { useState } from 'react';
import { 
  HeartPulse, 
  Flame, 
  Droplet, 
  Wind, 
  Sparkles, 
  ArrowLeft, 
  ArrowRight,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCart } from '../../context/CartContext';
import { useStore } from '../../context/StoreContext';
import { formatPrice } from '../../utils/formatters';

const TIBBI_ARCHETYPES = [
  {
    id: 'asabi-azlati',
    nameUrdu: 'اعصابی عضلاتی (ترسرد)',
    nameEn: 'Nervous-Muscular (Moist & Cold)',
    organUrdu: 'دماغ و اعصاب کی مشینی تحریک (تحریک نمبر 1)',
    pulseUrdu: 'نبض موٹی، گہری، سست اور ٹھنڈی',
    icon: Droplet,
    color: 'from-blue-600 to-cyan-700',
    symptomsUrdu: 'پیشاب کا بار بار اور کثرت سے سفید آنا، ذیابیطس (شوگر)، بلڈ پریشر لو، بلغم، یادداشت کی کمزوری، موٹاپا، جوڑوں کی سوجن۔',
    remedyPrincipleUrdu: 'عضلاتی اعصابی و عضلاتی غدی ادویات (حرارت و خشکی پیدا کرنا، اعصاب کو سکیڑ کر توانائی لانا، مثلاً معجون فلاسفہ، حب مقوی، کلونجی خالص، سلاجیت) نافع ہیں۔',
    recommendedProductIds: ['prod-1', 'prod-8', 'prod-9']
  },
  {
    id: 'azlati-asabi',
    nameUrdu: 'عضلاتی اعصابی (خشک سرد)',
    nameEn: 'Muscular-Nervous (Dry & Cold)',
    organUrdu: 'قلب و عضلات کی مشینی تحریک (تحریک نمبر 2)',
    pulseUrdu: 'نبض موٹی، تنی ہوئی اور انگلیوں پر ابھری ہوئی',
    icon: Wind,
    color: 'from-amber-800 to-stone-900',
    symptomsUrdu: 'دائمی قبض، پیٹ میں شدید گیس و ریاح، تبخیر معدہ، دل کی گھبراہٹ، نیند کی کمی، پٹھوں کا کھچاؤ، بواسیر بادی، تیزابیت۔',
    remedyPrincipleUrdu: 'عضلاتی غدی یا غدی عضلاتی ادویات (حرارت و صفرا پیدا کر کے تیزابیت کو توڑنا، مثلاً تریاق معدہ، ملین عضلاتی، جوارش کمونی) اکسیر ہیں۔',
    recommendedProductIds: ['prod-2', 'prod-6', 'prod-9']
  },
  {
    id: 'azlati-ghaddi',
    nameUrdu: 'عضلاتی غدی (خشک گرم)',
    nameEn: 'Muscular-Glandular (Dry & Hot)',
    organUrdu: 'قلب و عضلات کی کیمیاوی تحریک (تحریک نمبر 3)',
    pulseUrdu: 'نبض سخت، تیز رفتار اور لمبی',
    icon: Flame,
    color: 'from-orange-700 to-amber-900',
    symptomsUrdu: 'کھٹی ڈکاریں، سینے اور معدے کی شدید جلن و تیزابیت، یورک ایسڈ، جوڑوں کے درد، خونی بواسیر، خارش، الرجی۔',
    remedyPrincipleUrdu: 'غدی عضلاتی و غدی اعصابی ادویات (جگر میں تحریک پیدا کر کے عضلات کی جلن دور کرنا، مثلاً ملین غدی، حب کبد، عرق مکو) نافع ہیں۔',
    recommendedProductIds: ['prod-6', 'prod-7', 'prod-10']
  },
  {
    id: 'ghaddi-azlati',
    nameUrdu: 'غدی عضلاتی (گرم خشک)',
    nameEn: 'Glandular-Muscular (Hot & Dry)',
    organUrdu: 'جگر و غدد کی مشینی تحریک (تحریک نمبر 4)',
    pulseUrdu: 'نبض چار انگلیوں پر، باریک اور گرم',
    icon: Flame,
    color: 'from-amber-600 to-orange-700',
    symptomsUrdu: 'صفرا کی زیادتی، منہ کا کڑوا ذائقہ، شدید پیاس، پیشاب کی زردی و جلن، بلڈ پریشر ہائی، یرقان، غصہ و چڑچڑاپن۔',
    remedyPrincipleUrdu: 'غدی اعصابی و اعصابی غدی ادویات (رطوبت اور تری پیدا کر کے صفرا کی جلن مبرد کرنا، مثلاً تریاق جگر، شربت بزوری، تخم ریحان) بہترین ہیں۔',
    recommendedProductIds: ['prod-4', 'prod-5', 'prod-14']
  },
  {
    id: 'ghaddi-asabi',
    nameUrdu: 'غدی اعصابی (گرم تر)',
    nameEn: 'Glandular-Nervous (Hot & Moist)',
    organUrdu: 'جگر و غدد کی کیمیاوی تحریک (تحریک نمبر 5)',
    pulseUrdu: 'نبض چوڑی، نرم اور حرارت سے بھرپور',
    icon: Sparkles,
    color: 'from-rose-600 to-red-800',
    symptomsUrdu: 'خون کی حدت، جسم میں اندرونی گرمی، پھوڑے، پھنسیاں، الرجی، نکسیر، چہرے کے دانے، کثرتِ پسینہ۔',
    remedyPrincipleUrdu: 'اعصابی غدی و اعصابی عضلاتی ادویات (مصفی خون، حرارت کو پسینے اور پیشاب سے خارج کرنا، مثلاً مصفی خون، شربت صندل، عرق کاسنی) مفید ہیں۔',
    recommendedProductIds: ['prod-7', 'prod-12', 'prod-16']
  },
  {
    id: 'asabi-ghaddi',
    nameUrdu: 'اعصابی غدی (تر گرم)',
    nameEn: 'Nervous-Glandular (Moist & Hot)',
    organUrdu: 'دماغ و اعصاب کی کیمیاوی تحریک (تحریک نمبر 6)',
    pulseUrdu: 'نبض نرم، گہری اور دھیمی',
    icon: Droplet,
    color: 'from-teal-600 to-emerald-800',
    symptomsUrdu: 'تھوک و رطوبات کی کثرت، ہاضمے کی سستی، ڈھیلا پن، نرم پاخانے، قے، لاغری، سستی و کمزوری۔',
    remedyPrincipleUrdu: 'عضلاتی اعصابی ادویات (خشکی و قوت پیدا کرنا، ریاح کو خارج کر کے ہاضمہ بحال کرنا، مثلاً جوارش، تریاق اعصاب، لونگ دارچینی قہوہ) اکسیر ہیں۔',
    recommendedProductIds: ['prod-3', 'prod-8', 'prod-11']
  }
];

const TibbiDiagnosisGuide = ({ products }) => {
  const { lang, isRtl } = useLanguage();
  const { setSelectedProductModal, viewProductDetail } = useCart();
  const { cmsContent } = useStore();
  const [activeArchetypeId, setActiveArchetypeId] = useState('asabi-azlati');

  // Merge default archetypes with CMS content so any CMS updates are reflected
  const activeArchetypes = TIBBI_ARCHETYPES.map(defaultArch => {
    const custom = (cmsContent?.tibbiDiagnosis?.archetypes || []).find(a => a.id === defaultArch.id);
    if (!custom) return defaultArch;
    return {
      ...defaultArch,
      nameUrdu: custom.nameUrdu || defaultArch.nameUrdu,
      nameEn: custom.nameEn || defaultArch.nameEn,
      organUrdu: custom.organUrdu || defaultArch.organUrdu,
      pulseUrdu: custom.pulseUrdu || defaultArch.pulseUrdu,
      symptomsUrdu: custom.symptomsUrdu || defaultArch.symptomsUrdu,
      remedyPrincipleUrdu: custom.remedyPrincipleUrdu || defaultArch.remedyPrincipleUrdu
    };
  });

  const activeArchetype = activeArchetypes.find(a => a.id === activeArchetypeId) || activeArchetypes[0];
  const recommendedProducts = products.filter(p => activeArchetype.recommendedProductIds.includes(p.id));

  const sectionTag = cmsContent?.tibbiDiagnosis?.sectionTagUrdu || 'قانون مفرد اعضاء (طبِ پاکستانی) تشخیصی گائیڈ';
  const sectionTitle = lang === 'ur'
    ? (cmsContent?.tibbiDiagnosis?.titleUrdu || 'اعضائے رئیسہ (اعصاب، عضلات، غدد) اور نبض کے مطابق دوا تلاش کریں')
    : (cmsContent?.tibbiDiagnosis?.titleEn || 'Identify Pathology by Elementary Organs & Pulse (Mizaj)');
  const sectionDesc = lang === 'ur'
    ? (cmsContent?.tibbiDiagnosis?.descUrdu || 'طبِ پاکستانی میں بیماری کا سبب تینوں مفرد اعضاء (اعصاب، عضلات، غدد) میں تحریک، تسکین یا تحلیل کا بگاڑ ہے۔ اپنی علامات یا نبض منتخب کر کے اصولِ علاج جانیے:')
    : (cmsContent?.tibbiDiagnosis?.descEn || 'In Tibb-e-Pakistani, health is the harmony of elementary organs. Select your symptoms to understand root causes and remedy:');

  return (
    <section className="py-14 bg-white border-t border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold font-title">
            <HeartPulse className="w-3.5 h-3.5 text-emerald-700" />
            <span>{sectionTag}</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-black text-pansar-950 font-title">
            {sectionTitle}
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 font-urdu leading-relaxed">
            {sectionDesc}
          </p>

          {/* 3 Master Organs Indicator */}
          <div className="pt-2 flex flex-wrap items-center justify-center gap-2 text-xs font-urdu">
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200">
              {lang === 'ur' 
                ? '❤️ قلب و عضلات: مرکز حرکت و عضلاتی تحریک (خشکی)' 
                : '❤️ Heart & Muscular System: Muscular Action (Dryness)'}
            </span>
            <span className="px-3 py-1 rounded-full bg-rose-50 text-rose-900 border border-rose-200">
              {lang === 'ur' 
                ? '🫀 جگر و غدد: مرکز حرارت و غدی تحریک (گرمی)' 
                : '🫀 Liver & Glands: Glandular Heat & Metabolism (Heat)'}
            </span>
            <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-900 border border-blue-200">
              {lang === 'ur' 
                ? '🧠 دماغ و اعصاب: مرکز احساس و اعصابی تحریک (تری)' 
                : '🧠 Brain & Nervous System: Neural Transmission (Moisture)'}
            </span>
          </div>
        </div>

        {/* 6 Archetypes Selector Tabs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3 mb-8">
          {activeArchetypes.map((arch) => {
            const Icon = arch.icon;
            const isSelected = arch.id === activeArchetypeId;
            return (
              <button
                key={arch.id}
                onClick={() => setActiveArchetypeId(arch.id)}
                className={`p-3 sm:p-4 rounded-2xl border text-start transition-all transform duration-200 flex flex-col justify-between cursor-pointer ${
                  isSelected
                    ? 'border-pansar-700 bg-pansar-900 text-white shadow-lg scale-[1.02]'
                    : 'border-gray-200 bg-gray-50/80 hover:bg-gray-100 text-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-amber-400 text-slate-950' : 'bg-gray-200 text-gray-700'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                  )}
                </div>
                <div>
                  <h4 className="font-extrabold text-xs sm:text-sm font-title leading-snug">
                    {lang === 'ur' ? arch.nameUrdu : arch.nameEn}
                  </h4>
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected Archetype Diagnostic Box */}
        <div className="bg-pansar-50/70 border border-pansar-200 rounded-3xl p-6 sm:p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            
            <div className="md:col-span-7 space-y-3.5 text-start">
              
              {/* Organ and Pulse details */}
              <div className="flex flex-wrap items-center gap-2">
                {activeArchetype.organUrdu && (
                  <span className="text-xs font-bold text-amber-900 font-urdu bg-amber-100 px-3 py-1 rounded-lg border border-amber-200">
                    {lang === 'ur' ? `متاثرہ عضو: ${activeArchetype.organUrdu}` : `Elementary Organ: ${activeArchetype.nameEn}`}
                  </span>
                )}
                {activeArchetype.pulseUrdu && (
                  <span className="text-xs font-bold text-blue-900 font-urdu bg-blue-100 px-3 py-1 rounded-lg border border-blue-200">
                    {lang === 'ur' ? `کیفیتِ نبض: ${activeArchetype.pulseUrdu}` : `Pulse Quality: ${activeArchetype.pulseUrdu}`}
                  </span>
                )}
              </div>

              <div>
                <span className="text-xs font-bold text-pansar-800 font-urdu bg-pansar-200/70 px-2.5 py-1 rounded-md block w-fit mb-1.5">
                  {lang === 'ur' ? 'عام علامات و کیفیات (تحریک کی علامت):' : 'Key Diagnostic Symptoms:'}
                </span>
                <p className="text-sm sm:text-base text-gray-800 font-urdu leading-relaxed">
                  {activeArchetype.symptomsUrdu}
                </p>
              </div>
              
              <div className="pt-2 border-t border-pansar-200/60">
                <span className="text-xs font-bold text-emerald-800 font-urdu bg-emerald-100 px-2.5 py-1 rounded-md block w-fit mb-1.5">
                  {lang === 'ur' ? 'قانون مفرد اعضاء اصولِ علاج (علاج بالضد):' : 'Principles of Treatment:'}
                </span>
                <p className="text-xs sm:text-sm text-gray-700 font-urdu leading-relaxed">
                  {activeArchetype.remedyPrincipleUrdu}
                </p>
              </div>
            </div>

            <div className="md:col-span-5 bg-white rounded-2xl p-4 sm:p-5 border border-pansar-100 shadow-sm text-start">
              <h4 className="font-bold text-xs sm:text-sm text-pansar-900 font-urdu mb-3 flex items-center gap-1.5 justify-start">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span>{lang === 'ur' ? 'مجوزہ مستند ادویات برائے یہ مزاج:' : 'Recommended Formulations:'}</span>
              </h4>

              <div className="space-y-2.5">
                {recommendedProducts.map(p => (
                  <div
                    key={p.id}
                    onClick={() => {
                      if (viewProductDetail) {
                        viewProductDetail(p);
                      } else {
                        setSelectedProductModal(p);
                      }
                    }}
                    className="p-2.5 rounded-xl bg-gray-50 hover:bg-pansar-100 transition cursor-pointer flex items-center gap-3 border border-gray-100"
                  >
                    <img src={p.image} alt={p.nameUrdu} className="w-11 h-11 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <h5 className="font-bold text-xs text-gray-900 font-urdu truncate">
                        {lang === 'ur' ? p.nameUrdu : p.nameEn}
                      </h5>
                      <span className="text-[11px] font-extrabold text-pansar-800 font-sans">
                        {formatPrice(p.variants?.[0]?.price || p.price, lang)}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-700 font-urdu px-2 py-0.5 rounded bg-emerald-50">
                      {lang === 'ur' ? 'دیکھیں' : 'View'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default TibbiDiagnosisGuide;
